import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { User, Mail, AtSign, Save, Loader2, Shield, Calendar, Check, X, Info, Lock, Eye, EyeOff } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const usernameRules = [
  { id: 'length', label: '3-20 characters', test: (u) => u.length >= 3 && u.length <= 20 },
  { id: 'chars', label: 'Only letters, numbers, and underscores', test: (u) => /^[a-zA-Z0-9_]+$/.test(u) },
];

const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'At least 1 uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'At least 1 lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'At least 1 number', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'At least 1 special character (!@#$%^&*)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [donorAlias, setDonorAlias] = useState('');
  
  // Password change state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setDonorAlias(user.donor_alias || '');
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validate username if provided
    if (username && !usernameRules.every(rule => rule.test(username))) {
      toast.error('Username does not meet requirements');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.patch('/me', {
        username: username.trim() || null,
        donor_alias: donorAlias.trim() || null,
      });
      
      updateUser(response.data);
      toast.success('Profile updated!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (!passwordRules.every(rule => rule.test(newPassword))) {
      toast.error('New password does not meet requirements');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  const createdAt = new Date(user.created_at);

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">My Profile</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account settings
          </p>
        </div>

        <div className="space-y-6">
          {/* Account Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                </div>
                {user.email_verified && (
                  <Badge className="bg-green-100 text-green-800">Verified</Badge>
                )}
              </div>

              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Member Since</p>
                    <p className="font-medium">
                      {createdAt.toLocaleDateString('en-MY', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {user.is_admin && (
                <div className="flex items-center gap-3 p-4 bg-primary/10 rounded-xl">
                  <Shield className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-primary">Administrator</p>
                    <p className="text-sm text-muted-foreground">
                      You have admin privileges
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Profile Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AtSign className="w-5 h-5" />
                Profile Settings
              </CardTitle>
              <CardDescription>
                Customize how you appear on Fundfair
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Choose a username"
                    className="rounded-xl"
                    data-testid="username-input"
                  />
                  
                  {/* Privacy notice */}
                  <div className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>This name will be visible to everyone. You do not need to use your real name.</span>
                  </div>

                  {/* Username requirements */}
                  {username && (
                    <div className="mt-2 p-3 bg-secondary/50 rounded-xl space-y-1.5">
                      {usernameRules.map((rule) => {
                        const passed = rule.test(username);
                        return (
                          <div
                            key={rule.id}
                            className={`flex items-center gap-2 text-sm ${
                              passed ? 'text-green-600' : 'text-muted-foreground'
                            }`}
                          >
                            {passed ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            {rule.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="donorAlias">Donor Name</Label>
                  <Input
                    id="donorAlias"
                    value={donorAlias}
                    onChange={(e) => setDonorAlias(e.target.value)}
                    placeholder="Name shown when you donate"
                    className="rounded-xl"
                    data-testid="donor-alias-input"
                  />
                  <p className="text-sm text-muted-foreground">
                    This name will be shown to campaign owners when you donate
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full"
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="rounded-xl pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="rounded-xl pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Password requirements */}
                  {newPassword && (
                    <div className="mt-2 p-3 bg-secondary/50 rounded-xl space-y-1.5">
                      {passwordRules.map((rule) => {
                        const passed = rule.test(newPassword);
                        return (
                          <div
                            key={rule.id}
                            className={`flex items-center gap-2 text-sm ${
                              passed ? 'text-green-600' : 'text-muted-foreground'
                            }`}
                          >
                            {passed ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            {rule.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="rounded-xl"
                    data-testid="confirm-password-input"
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <X className="w-4 h-4" />
                      Passwords do not match
                    </p>
                  )}
                  {confirmPassword && newPassword === confirmPassword && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Passwords match
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={passwordLoading}
                  className="w-full rounded-full"
                  data-testid="change-password-btn"
                >
                  {passwordLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}
